import { useState } from "react";
import { useDarkMode } from "../../../../../context/ThemeContext";
import { useIsReseller } from "../../../../../hooks/useIsReseller";
import { useTrailerForm } from "./hooks";
import {
  TrailerHeader,
  TrailerFilters,
  TrailerList,
  TrailerPagination,
  TrailerDetailsModal,
  TrailerEditModal,
  TrailerAddModal,
  DeviceConnectionModal,
} from "./components";
import type { ThemeClasses, Trailer } from "./types";
import { useTranslation } from "react-i18next";
import { useDevicesStore } from "../../../../../store/admin/devicesStore";
import { useTrailersStore } from "../../../../../store/admin/trailersStore";
import { useAlert } from "../../../../../context/AlertContext";

export default function TrailersSection() {
  const { isDarkMode } = useDarkMode();
  const { isReseller } = useIsReseller();
  const { t } = useTranslation("adminSections");
  const { showAlert } = useAlert();
  const attachRemolqueToDevice = useDevicesStore((state) => state.attachRemolqueToDevice);
  const detachRemolqueFromDevice = useDevicesStore((state) => state.detachRemolqueFromDevice);
  const deleteTrailer = useTrailersStore((state) => state.deleteTrailer);

  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [deviceTrailer, setDeviceTrailer] = useState<Trailer | null>(null);
  const {
    // Data
    trailers,
    meta,
    loading,
    error,
    invalidateTrailers,
    selectedTrailer,
    loadingSubmit,
    imagePreview,
    shipmentDetails,

    // Form
    register,
    errors,
    setValue,
    setError,
    clearErrors,
    watchedValues,

    // Modal states
    showDetailsModal,
    showEditModal,
    showCreateModal,

    // Filter states
    selectedFilter,
    selectedType,
    searchQuery,
    selectedView,
    currentPage,

    // Handlers
    handleEdit,
    handleDetails,
    handleOpenCreateModal,
    handleCloseModals,
    handleImageChange,
    handleImageDelete,
    handleAddShipmentDetail,
    handleRemoveShipmentDetail,
    handleShipmentDetailChange,
    handleEditSubmit,
    handleCreateSubmit,
    handleFilterChange,
    handleTypeChange,
    handleSearchChange,
    setSelectedView,
    setCurrentPage,
  } = useTrailerForm();

  const handleDeviceClick = (trailer: Trailer) => {
    setDeviceTrailer(trailer);
    setShowDeviceModal(true);
  };

  const handleDeleteTrailer = (trailer: Trailer) => {
    showAlert({
      title: t("trailers.delete.confirmTitle", "¿Eliminar remolque?"),
      text: t("trailers.delete.confirmText", {
        name: trailer.name || trailer.plate || "",
        defaultValue: "Esta acción marcará el remolque como eliminado.",
      }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("trailers.delete.confirm", "Eliminar"),
      cancelButtonText: t("trailers.form.cancel", "Cancelar"),
      confirmButtonColor: "#ef4444",
      onConfirm: async () => {
        const result = await deleteTrailer(trailer.id);
        if (result.success) {
          invalidateTrailers();
          showAlert({
            title: t("trailers.delete.successTitle", "Éxito"),
            text: t("trailers.delete.success", "Remolque eliminado correctamente"),
            icon: "success",
            confirmButtonText: "OK",
            confirmButtonColor: "#2563eb",
            onConfirm: () => {},
          });
        } else {
          showAlert({
            title: t("trailers.form.error") || "Error",
            text: result.error || t("trailers.delete.error", "No se pudo eliminar"),
            icon: "error",
            confirmButtonText: "OK",
            confirmButtonColor: "#2563eb",
            onConfirm: () => {},
          });
        }
      },
    });
  };

  const handleConnectDevice = async (deviceId: string) => {
    if (!deviceTrailer) return;
    const result = await attachRemolqueToDevice(deviceId, deviceTrailer.id);
    if (result?.success) {
      showAlert({
        title: t("trailers.device.connectSuccess"),
        text: t("trailers.device.connectSuccess"),
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        onConfirm: () => {
          invalidateTrailers();
        },
      });
    } else {
      showAlert({
        title: t("trailers.form.error") || "Error",
        text: result?.error || t("trailers.device.connectError"),
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        onConfirm: () => {},
      });
    }
  };

  const handleDisconnectDevice = async (deviceId: string) => {
    const result = await detachRemolqueFromDevice(deviceId);
    if (result?.success) {
      showAlert({
        title: t("trailers.device.disconnectSuccess"),
        text: t("trailers.device.disconnectSuccess"),
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        onConfirm: () => {
          invalidateTrailers();
        },
      });
    } else {
      showAlert({
        title: t("trailers.form.error") || "Error",
        text: result?.error || t("trailers.device.disconnectError"),
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        onConfirm: () => {},
      });
    }
  };

  const themeClasses: ThemeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  const totalPages = meta?.totalPages || 1;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <TrailerHeader
        themeClasses={themeClasses}
        onCreateTrailer={handleOpenCreateModal}
        t={t}
      />

      {/* Filters */}
      <TrailerFilters
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        selectedFilter={selectedFilter}
        selectedType={selectedType}
        searchQuery={searchQuery}
        onFilterChange={handleFilterChange}
        onTypeChange={handleTypeChange}
        onSearchChange={handleSearchChange}
        onViewChange={setSelectedView}
        selectedView={selectedView}
        t={t}
      />

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        ) : error ? (
          <div className="text-center text-red-500">{error}</div>
        ) : (
          <>
            <TrailerList
              trailers={trailers as any}
              themeClasses={themeClasses}
              selectedView={selectedView}
              onEdit={handleEdit}
              onDetails={handleDetails}
              onDeviceClick={handleDeviceClick}
              onDelete={isReseller ? handleDeleteTrailer : undefined}
              t={t}
            />

            {trailers.length > 0 && (
              <TrailerPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                t={t}
              />
            )}
          </>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedTrailer && (
        <TrailerDetailsModal
          trailer={selectedTrailer}
          themeClasses={themeClasses}
          onClose={handleCloseModals}
          onEdit={handleEdit}
          t={t}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <TrailerEditModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          register={register}
          errors={errors}
          loadingSubmit={loadingSubmit}
          onClose={handleCloseModals}
          onSubmit={handleEditSubmit}
          onImageChange={handleImageChange}
          onImageDelete={handleImageDelete}
          imagePreview={imagePreview}
          t={t}
          setValue={setValue}
          watchedValues={watchedValues}
          shipmentDetails={shipmentDetails}
          onAddShipmentDetail={handleAddShipmentDetail}
          onRemoveShipmentDetail={handleRemoveShipmentDetail}
          onShipmentDetailChange={handleShipmentDetailChange}
        />
      )}

      {/* Add Modal */}
      {showCreateModal && (
        <TrailerAddModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          register={register}
          errors={errors}
          loadingSubmit={loadingSubmit}
          onClose={handleCloseModals}
          onSubmit={handleCreateSubmit}
          onImageChange={handleImageChange}
          imagePreview={imagePreview}
          t={t}
          setValue={setValue}
          setError={setError}
          clearErrors={clearErrors}
          watchedValues={watchedValues}
          shipmentDetails={shipmentDetails}
          onAddShipmentDetail={handleAddShipmentDetail}
          onRemoveShipmentDetail={handleRemoveShipmentDetail}
          onShipmentDetailChange={handleShipmentDetailChange}
        />
      )}

      {/* Device Connection Modal */}
      {showDeviceModal && deviceTrailer && (
        <DeviceConnectionModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          trailer={deviceTrailer}
          onClose={() => {
            setShowDeviceModal(false);
            setDeviceTrailer(null);
          }}
          onConnect={handleConnectDevice}
          onDisconnect={handleDisconnectDevice}
        />
      )}
    </div>
  );
}
